import './messageHandlers/register';
import { createSlice, Middleware, createSelector } from '@reduxjs/toolkit';
import { ImageDocumentStoreItem, DocumentCategories, ImageContext } from '@v2/components/imageHandling/types/types';
import { setExpirationTimeAsTimestamp } from '@helpers';
import { getImageContextKeyForMultipleImages, getImageContextFromContextKey } from '@v2/components/imageHandling/helpers';
import { fetchDocumentCategories, fetchImageDocumentById, fetchImagesDocuments, processImageDocumentMessages } from './thunks';
import {
  addOrUpdateImageDocumentAction,
  changePDFImageAction,
  removeImageDocumentFromDictAction,
  removeImageDocumentFromSpecificContextAction,
  setCurrentContextKeyAction,
  setDocumentCategoriesAction,
  setIsNoteAddingAction,
  setNotSubmittedDeletedImageAction,
  setPreImageHandlingScreenAction,
  setSelectedNoteIdAction,
  setUpdatingImageMetadataInProgressAction,
  updateImageDocumentInDictAction,
} from './actions';
import { AppDispatch } from '@store/types';
import { FORCE_RETRIEVE_FROM_SOURCE_KEY_EXPIRATION_TIME, TTL_FOR_IMAGE } from '@v2/components/imageHandling/constants';

export interface DocumentStore {
  documentIds: number[];
  ttl: number;
  fetchContext?: {
    retrieveAllDocuments: boolean;
    forceRetrieveFromSource: boolean;
  };
  loading: boolean;
  error: string | null;
}

export interface ImageHandlingState {
  documentCategories: DocumentCategories;
  imagesDocumentsContexts: Record<string, DocumentStore>;
  imagesDocumentsDict: Record<number, ImageDocumentStoreItem>;
  selectedNoteId: number | null;
  isNoteAdding: boolean;
  updatingImageMetadataInProgress: boolean;
  currentContextKey: string | null;
  preImageHandlingScreen: Record<string, unknown> | null;
  notSubmittedDeletedImage: { document: ImageDocumentStoreItem; imageContext: ImageContext } | null;
}

const initialState: ImageHandlingState = {
  documentCategories: {
    loaded: false,
    items: [],
  },
  imagesDocumentsContexts: {},
  imagesDocumentsDict: {},
  selectedNoteId: null,
  isNoteAdding: false,
  updatingImageMetadataInProgress: false,
  currentContextKey: null,
  preImageHandlingScreen: null,
  notSubmittedDeletedImage: null,
};

// Helper to refresh forceRetrieveFromSource timestamp
export const refreshForceRetrieveFromSource = () => {
  return setExpirationTimeAsTimestamp(FORCE_RETRIEVE_FROM_SOURCE_KEY_EXPIRATION_TIME);
};

export const isImageContextHasScopeId = (imageContext: ImageContext) => 'scopeId' in imageContext;
export const isImageContextHasScopeReferenceId = (imageContext: ImageContext) => 'scopeReferenceId' in imageContext;
export const isImageContextHasScopeIdAndScopeReferenceId = (imageContext: ImageContext) =>
  isImageContextHasScopeId(imageContext) && isImageContextHasScopeReferenceId(imageContext);

const imageHandlingSlice = createSlice({
  name: 'imageHandlingV2',
  initialState,
  reducers: {
    addOrUpdateImageDocument: addOrUpdateImageDocumentAction,
    changePDFImage: changePDFImageAction,
    setDocumentCategories: setDocumentCategoriesAction,
    updateImageDocumentInDict: updateImageDocumentInDictAction,
    setUpdatingImageMetadataInProgress: setUpdatingImageMetadataInProgressAction,
    setCurrentContextKey: setCurrentContextKeyAction,
    removeImageDocumentFromDict: removeImageDocumentFromDictAction,
    removeImageDocumentFromSpecificContext: removeImageDocumentFromSpecificContextAction,
    setPreImageHandlingScreen: setPreImageHandlingScreenAction,
    setNotSubmittedDeletedImage: setNotSubmittedDeletedImageAction,
    setSelectedNoteId: setSelectedNoteIdAction,
    setIsNoteAdding: setIsNoteAddingAction,
  },
  extraReducers: (builder) => {
    builder
      // Fetch images documents
      .addCase(fetchImagesDocuments.pending, (state, action) => {
        const { scopeId, scopeReferenceId } = action.meta.arg;
        const key = getImageContextKeyForMultipleImages({ scopeId, scopeReferenceId });
        if (!state.imagesDocumentsContexts[key]) {
          state.imagesDocumentsContexts[key] = {
            documentIds: [],
            ttl: 0,
            fetchContext: undefined,
            loading: true,
            error: null,
          };
        } else {
          state.imagesDocumentsContexts[key].loading = true;
          state.imagesDocumentsContexts[key].error = null;
        }
      })
      .addCase(fetchImagesDocuments.fulfilled, (state, action) => {
        if (action.payload) {
          const { key, data, cached, fetchContext } = action.payload;

          // Store images in dictionary and collect document IDs
          const documentIds: number[] = [];
          data?.forEach((imageDoc: ImageDocumentStoreItem) => {
            state.imagesDocumentsDict[imageDoc.documentId] = imageDoc;
            documentIds.push(imageDoc.documentId);
          });

          state.imagesDocumentsContexts[key] = {
            documentIds: documentIds,
            ttl: cached ? state.imagesDocumentsContexts[key]?.ttl || 0 : Date.now() + TTL_FOR_IMAGE, // 2 minutes TTL
            fetchContext: fetchContext,
            loading: false,
            error: null,
          };
        }
      })
      .addCase(fetchImagesDocuments.rejected, (state, action) => {
        const { scopeId, scopeReferenceId } = action.meta.arg;
        const key = getImageContextKeyForMultipleImages({ scopeId, scopeReferenceId });
        if (!state.imagesDocumentsContexts[key]) {
          state.imagesDocumentsContexts[key] = {
            documentIds: [],
            ttl: 0,
            fetchContext: undefined,
            loading: false,
            error: action.error.message || 'Failed to fetch image documents',
          };
        } else {
          state.imagesDocumentsContexts[key].loading = false;
          state.imagesDocumentsContexts[key].error = action.error.message || 'Failed to fetch image documents';
        }
      })
      // Fetch single image document by ID
      .addCase(fetchImageDocumentById.pending, (state, action) => {
        const documentId = action.meta.arg; // This is the documentId passed to fetchImageDocumentById
        const key = String(documentId);
        if (!state.imagesDocumentsContexts[key]) {
          state.imagesDocumentsContexts[key] = {
            documentIds: [],
            ttl: 0,
            loading: true,
            error: null,
          };
        } else {
          state.imagesDocumentsContexts[key].loading = true;
          state.imagesDocumentsContexts[key].error = null;
        }
      })
      .addCase(fetchImageDocumentById.fulfilled, (state, action) => {
        if (action.payload) {
          const { documentId, data, cached } = action.payload;
          const key = String(documentId);

          // Store images in dictionary and collect document IDs
          const documentIds: number[] = [];
          data?.forEach((imageDoc: ImageDocumentStoreItem) => {
            state.imagesDocumentsDict[imageDoc.documentId] = imageDoc;
            documentIds.push(imageDoc.documentId);
          });

          state.imagesDocumentsContexts[key] = {
            documentIds: documentIds,
            ttl: cached ? state.imagesDocumentsContexts[key]?.ttl || 0 : Date.now() + TTL_FOR_IMAGE,
            loading: false,
            error: null,
          };
        }
      })
      .addCase(fetchImageDocumentById.rejected, (state, action) => {
        const documentId = action.meta.arg; // This is the documentId passed to fetchImageDocumentById
        const key = String(documentId);
        if (!state.imagesDocumentsContexts[key]) {
          state.imagesDocumentsContexts[key] = {
            documentIds: [],
            ttl: 0,
            loading: false,
            error: action.error.message || 'Failed to fetch image document',
          };
        } else {
          state.imagesDocumentsContexts[key].loading = false;
          state.imagesDocumentsContexts[key].error = action.error.message || 'Failed to fetch image document';
        }
      })
      // Fetch Document Categories
      .addCase(fetchDocumentCategories.pending, (state) => {
        state.documentCategories = {
          loaded: false,
          items: [],
        };
      })
      .addCase(fetchDocumentCategories.fulfilled, (state, action) => {
        state.documentCategories = {
          loaded: true,
          items: action.payload,
        };
      })
      .addCase(fetchDocumentCategories.rejected, (state, action) => {
        state.documentCategories = {
          loaded: false,
          items: [],
        };
        console.error('Failed to fetch document categories:', action.error.message);
      });
  },
});

// Middleware to handle post-fetch message processing for image documents
export const messageHandlerMiddleware: Middleware = (store) => (next) => (action) => {
  const result = next(action);

  // Process image document messages after successful fetch operations
  if (action.type === fetchImagesDocuments.fulfilled.type && action.payload?.data?.length) {
    const { data } = action.payload;
    // Schedule message processing for next tick to ensure store is updated
    setTimeout(() => {
      for (const imageDocument of data) {
        (store.dispatch as AppDispatch)(processImageDocumentMessages({ imageDocument }));
      }
    }, 0);
  }

  if (action.type === fetchImageDocumentById.fulfilled.type && action.payload?.data?.length) {
    const { data } = action.payload;
    // Schedule message processing for next tick to ensure store is updated
    setTimeout(() => {
      for (const imageDocument of data) {
        (store.dispatch as AppDispatch)(processImageDocumentMessages({ imageDocument }));
      }
    }, 0);
  }

  return result;
};

// Export actions
export const {
  addOrUpdateImageDocument,
  changePDFImage,
  setDocumentCategories,
  setUpdatingImageMetadataInProgress,
  updateImageDocumentInDict,
  setCurrentContextKey,
  removeImageDocumentFromDict,
  removeImageDocumentFromSpecificContext,
  setPreImageHandlingScreen,
  setNotSubmittedDeletedImage,
  setSelectedNoteId,
  setIsNoteAdding,
} = imageHandlingSlice.actions;

// Base selectors for memoization
const selectImagesDocumentsContexts = (state: { imageHandlingV2: ImageHandlingState }) => state.imageHandlingV2.imagesDocumentsContexts;
const selectImagesDocumentsDict = (state: { imageHandlingV2: ImageHandlingState }) => state.imageHandlingV2.imagesDocumentsDict;
const selectDocumentCategories = (state: { imageHandlingV2: ImageHandlingState }) => state.imageHandlingV2.documentCategories;
const selectUpdatingImageMetadataInProgress = (state: { imageHandlingV2: ImageHandlingState }) =>
  state.imageHandlingV2.updatingImageMetadataInProgress;
const selectSelectedNoteId = (state: { imageHandlingV2: ImageHandlingState }) => state.imageHandlingV2.selectedNoteId;
const selectIsNoteAdding = (state: { imageHandlingV2: ImageHandlingState }) => state.imageHandlingV2.isNoteAdding;

// Memoized selector factories (these return memoized selectors)
export const selectDocumentStore = (key: number | string) =>
  createSelector([selectImagesDocumentsContexts], (imagesDocumentsContexts) => imagesDocumentsContexts[String(key)]);

export const selectImagesDocuments = (key: number | string | null) =>
  createSelector([selectImagesDocumentsContexts, selectImagesDocumentsDict], (imagesDocumentsContexts, imagesDocumentsDict) => {
    if (key == null) return [];
    const documentStore = imagesDocumentsContexts[String(key)];
    if (!documentStore) return [];

    return documentStore.documentIds.map((docId: number) => imagesDocumentsDict[docId]).filter(Boolean);
  });

export const selectDocumentById = (documentId: number) =>
  createSelector([selectImagesDocumentsDict], (imagesDocumentsDict) => imagesDocumentsDict[documentId]);

const selectScopeIndexFromDict = createSelector([selectImagesDocumentsDict], (imagesDocumentsDict) => {
  const favoriteMap = new Map<string, ImageDocumentStoreItem>();
  const allScopeKeys = new Set<string>();
  for (const doc of Object.values(imagesDocumentsDict)) {
    if (!doc?.links) continue;
    const docTime = new Date(doc.createdOn ?? doc.modifiedOn ?? 0).getTime();
    for (const link of doc.links) {
      if (link.scopeId == null || link.scopeReferenceId == null) continue;
      const key = `${link.scopeId}_${link.scopeReferenceId}`;
      allScopeKeys.add(key);
      if (!link.isFavorite) continue;
      const existing = favoriteMap.get(key);
      const existingTime = existing ? new Date(existing.createdOn ?? existing.modifiedOn ?? 0).getTime() : -Infinity;
      if (docTime > existingTime) favoriteMap.set(key, doc);
    }
  }
  return { favoriteMap, allScopeKeys };
});

export const selectFavoriteImageByScope = (scopeId: number | undefined, scopeReferenceId: number | undefined) =>
  createSelector([selectScopeIndexFromDict], ({ favoriteMap }) =>
    scopeId == null || scopeReferenceId == null ? null : favoriteMap.get(`${scopeId}_${scopeReferenceId}`) ?? null
  );

export const selectScopeHasAnyImage = (scopeId: number | undefined, scopeReferenceId: number | undefined) =>
  createSelector([selectScopeIndexFromDict], ({ allScopeKeys }) =>
    scopeId == null || scopeReferenceId == null ? false : allScopeKeys.has(`${scopeId}_${scopeReferenceId}`)
  );

export const selectDocumentLoading = (key: number | string) => createSelector([selectDocumentStore(key)], (store) => store?.loading || false);

export const selectDocumentError = (key: number | string) => createSelector([selectDocumentStore(key)], (store) => store?.error || null);

export const selectDocumentTTL = (key: number | string) => createSelector([selectDocumentStore(key)], (store) => store?.ttl || 0);

export const selectDocumentFetchContext = (key: number | string) => createSelector([selectDocumentStore(key)], (store) => store?.fetchContext);

// Memoized selectors for general state
export const selectAllDocumentIds = createSelector([selectImagesDocumentsContexts], (imagesDocumentsContexts) =>
  Object.keys(imagesDocumentsContexts)
    .map(Number)
    .filter((id) => !isNaN(id))
);

// Export memoized selectors for document categories and metadata progress
export { selectDocumentCategories, selectUpdatingImageMetadataInProgress, selectSelectedNoteId, selectIsNoteAdding };

// Memoized selector to get the selected note image document
export const selectSelectedNote = createSelector([selectSelectedNoteId, selectImagesDocumentsDict], (selectedNoteId, imagesDocumentsDict) => {
  if (!selectedNoteId) return null;
  const documentId = selectedNoteId;
  return imagesDocumentsDict[documentId] || null;
});

export const selectCurrentImageDocumentContextKey = (state: { imageHandlingV2: ImageHandlingState }) => state.imageHandlingV2.currentContextKey;

// Memoized selector to get the current image document context
export const selectCurrentImageDocumentContext = createSelector([selectCurrentImageDocumentContextKey], (currentContextKey) => {
  return getImageContextFromContextKey(currentContextKey);
});

export const selectPreImageHandlingScreen = (state: { imageHandlingV2: ImageHandlingState }) => state.imageHandlingV2.preImageHandlingScreen;

// Memoized combined selector that gets the current context images directly
export const selectCurrentContextImagesDocuments = createSelector(
  [selectCurrentImageDocumentContextKey, selectImagesDocumentsContexts, selectImagesDocumentsDict],
  (currentContextKey, imagesDocumentsContexts, imagesDocumentsDict) => {
    if (!currentContextKey) return [];
    const documentStore = imagesDocumentsContexts[currentContextKey];
    if (!documentStore) return [];

    return documentStore.documentIds.map((docId: number) => imagesDocumentsDict[docId]).filter(Boolean);
  }
);

// Memoized combined selector that gets the current context document store
export const selectCurrentContextDocumentStore = createSelector(
  [selectCurrentImageDocumentContextKey, selectImagesDocumentsContexts],
  (currentContextKey, imagesDocumentsContexts) => {
    if (!currentContextKey) return undefined;
    return imagesDocumentsContexts[currentContextKey];
  }
);

// Memoized combined selector that gets the current context loading state
export const selectCurrentContextLoading = createSelector([selectCurrentContextDocumentStore], (documentStore) => documentStore?.loading ?? true);

// Memoized combined selector that gets the current context error state
export const selectCurrentContextError = createSelector([selectCurrentContextDocumentStore], (documentStore) => documentStore?.error || null);

// Memoized combined selector that gets the current context TTL
export const selectCurrentContextTTL = createSelector([selectCurrentContextDocumentStore], (documentStore) => documentStore?.ttl || 0);

// Memoized combined selector that gets the current context fetchContext
export const selectCurrentContextFetchContext = createSelector([selectCurrentContextDocumentStore], (documentStore) => documentStore?.fetchContext);

export default imageHandlingSlice.reducer;
